import { UnreachableCaseError } from '@lib/validation';
import { PriceSegmentType } from '@snap-types/models/price-segment';
import type { PriceSegmentConfigUpdate } from '@snap-types/models/price-segment-config';
import { dec } from '@snap-types/types/decimal';

export const priceSegmentTypesForSite: Record<string, PriceSegmentType[]> = {
  default: [
    PriceSegmentType.CustomerPotential,
    PriceSegmentType.CustomerPortals,
    PriceSegmentType.ProductOperationsCount,
    PriceSegmentType.ProductComplexity,
    PriceSegmentType.MarketIndustry,
    PriceSegmentType.LastTransactionAge,
    PriceSegmentType.LastGrossMargin,
  ],
};

export const priceSegmentConfigOptions = {
  [PriceSegmentType.CustomerPotential]: 'Customer Potential',
  [PriceSegmentType.CustomerPortals]: 'Customer Portals',
  [PriceSegmentType.ProductOperationsCount]: 'Product Operations Count',
  [PriceSegmentType.ProductComplexity]: 'Product Complexity',
  [PriceSegmentType.MarketIndustry]: 'Market Industry',
  [PriceSegmentType.LastTransactionAge]: 'Last Transaction Age',
  [PriceSegmentType.LastGrossMargin]: 'Last Gross Margin',
} as const satisfies Record<PriceSegmentType, string>;

export function getAvailablePriceSegmentConfigTypes(
  siteCode: string,
  existingTypes: PriceSegmentType[] = [],
): { all: PriceSegmentType[]; available: PriceSegmentType[] } {
  const siteAvailableTypes =
    priceSegmentTypesForSite[siteCode.toLowerCase()] ??
    priceSegmentTypesForSite.default;

  return {
    all: siteAvailableTypes,
    available: siteAvailableTypes.filter(
      (type) => !existingTypes.includes(type),
    ),
  };
}

export function getDefaultPriceSegmentParamsForType(
  type: PriceSegmentType,
): Extract<PriceSegmentConfigUpdate, { priceSegmentType: PriceSegmentType }> {
  switch (type) {
    case PriceSegmentType.CustomerPotential: {
      return {
        sortOrder: 1,
        enabled: true,
        priceSegmentType: PriceSegmentType.CustomerPotential,
        low: dec('0'),
        medium: dec('0'),
        high: dec('0'),
      };
    }

    case PriceSegmentType.CustomerPortals:
      return {
        sortOrder: 1,
        enabled: true,
        priceSegmentType: PriceSegmentType.CustomerPortals,
        portalsAdjustment: dec('0'),
      };

    case PriceSegmentType.ProductOperationsCount:
      return {
        sortOrder: 1,
        enabled: true,
        priceSegmentType: PriceSegmentType.ProductOperationsCount,
        gteAdjustment: dec('0'),
        ltAdjustment: dec('0'),
        threshold: 0,
      };

    case PriceSegmentType.ProductComplexity:
      return {
        sortOrder: 1,
        enabled: true,
        priceSegmentType: PriceSegmentType.ProductComplexity,
        simple: dec('0'),
        moderate: dec('0'),
        complex: dec('0'),
      };

    case PriceSegmentType.MarketIndustry:
      return {
        sortOrder: 1,
        enabled: true,
        priceSegmentType: PriceSegmentType.MarketIndustry,
        defaultAdjustment: dec('0'),
        marketIndustryAdjustments: [],
      };

    case PriceSegmentType.LastTransactionAge:
      return {
        sortOrder: 1,
        enabled: true,
        priceSegmentType: PriceSegmentType.LastTransactionAge,
        defaultAdjustment: dec('0'),
        lastTransactionAgeAdjustments: [],
      };

    case PriceSegmentType.LastGrossMargin:
      return {
        sortOrder: 1,
        enabled: true,
        priceSegmentType: PriceSegmentType.LastGrossMargin,
        defaultAdjustment: dec('0'),
        lastGrossMarginAdjustments: [],
      };

    default:
      throw new UnreachableCaseError(type);
  }
}
